import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiGetHangmanWord } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';

const MAX_WRONG = 6;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function HangmanSVG({ wrong }) {
  return (
    <svg className="hangman-svg" viewBox="0 0 200 220" fill="none" stroke="#6366f1" strokeWidth="4" strokeLinecap="round">
      {/* Gallows */}
      <line x1="20" y1="210" x2="180" y2="210"/>
      <line x1="60" y1="210" x2="60" y2="20"/>
      <line x1="60" y1="20" x2="130" y2="20"/>
      <line x1="130" y1="20" x2="130" y2="45"/>
      {/* Head */}
      {wrong >= 1 && <circle cx="130" cy="62" r="17" stroke="#ef4444"/>}
      {/* Body */}
      {wrong >= 2 && <line x1="130" y1="79" x2="130" y2="140" stroke="#ef4444"/>}
      {/* Left arm */}
      {wrong >= 3 && <line x1="130" y1="100" x2="100" y2="125" stroke="#ef4444"/>}
      {/* Right arm */}
      {wrong >= 4 && <line x1="130" y1="100" x2="160" y2="125" stroke="#ef4444"/>}
      {/* Left leg */}
      {wrong >= 5 && <line x1="130" y1="140" x2="100" y2="170" stroke="#ef4444"/>}
      {/* Right leg */}
      {wrong >= 6 && <line x1="130" y1="140" x2="160" y2="170" stroke="#ef4444"/>}
    </svg>
  );
}

function buildShareText(word, wrongCount, status) {
  if (status === 'won') {
    const lives = Array(6 - wrongCount).fill('❤️').join('') + Array(wrongCount).fill('🖤').join('');
    return `🎭 WordZingo Hangman\nI guessed "${word}" with only ${wrongCount} wrong letter${wrongCount !== 1 ? 's' : ''}!\n${lives}\n🎯 Can you beat me? wordzingo.com/hangman`;
  }
  return `🎭 WordZingo Hangman\nThe hangman got me! The word was "${word}" 💀\n🎯 wordzingo.com/hangman`;
}

export default function HangmanGame() {
  const [word, setWord]           = useState('');
  const [guessed, setGuessed]     = useState(new Set());
  const [status, setStatus]       = useState('loading'); // loading|playing|won|lost
  const [, setHint]               = useState('');
  const [showShare, setShowShare] = useState(false);

  async function newGame() {
    setStatus('loading'); setGuessed(new Set()); setHint(''); setShowShare(false);
    try {
      const { data } = await apiGetHangmanWord();
      setWord(data.word.toUpperCase());
      setStatus('playing');
    } catch {
      const fallback = ['PUZZLE','WIZARD','JUNGLE','PLANET','FLOWER','CASTLE','DRAGON','BRIDGE','SILVER','MARBLE'];
      setWord(fallback[Math.floor(Math.random()*fallback.length)]);
      setStatus('playing');
    }
  }

  useEffect(() => { newGame(); }, []);

  const wrongGuesses = [...guessed].filter(l => !word.includes(l));
  const wrongCount = wrongGuesses.length;

  const handleGuess = useCallback((letter) => {
    if (status !== 'playing' || guessed.has(letter)) return;
    const next = new Set([...guessed, letter]);
    setGuessed(next);
    const allRevealed = word.split('').every(ch => next.has(ch));
    if (allRevealed) { setStatus('won'); setTimeout(() => setShowShare(true), 1500); }
    else if ([...next].filter(l => !word.includes(l)).length >= MAX_WRONG) { setStatus('lost'); setTimeout(() => setShowShare(true), 1500); }
  }, [guessed, status, word]);

  useEffect(() => {
    function onKey(e) {
      const k = e.key.toUpperCase();
      if (/^[A-Z]$/.test(k)) handleGuess(k);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleGuess]);

  const displayWord = word.split('').map(ch => guessed.has(ch) ? ch : '_');

  return (
    <div className="game-page">
      <PageSEO page="hangman" />
      {showShare && (
        <ShareModal
          shareText={buildShareText(word, wrongCount, status)}
          gameUrl="https://wordzingo.com/hangman"
          onClose={() => setShowShare(false)}
          onNewGame={newGame}
        />
      )}
      <div className="container" style={{maxWidth:580}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🎭</span>
          <h1>Hangman</h1>
          <p>Guess the hidden word one letter at a time. Don't let the hangman finish!</p>
        </div>

        {status === 'loading' ? (
          <div className="text-center py-5"><div className="spinner-border" style={{color:'var(--primary)'}}></div></div>
        ) : (
          <div className="solver-card text-center">
            {/* Status bar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="badge fw-bold" style={{background:'#fee2e2',color:'#991b1b',fontSize:'0.9rem',padding:'0.4rem 0.8rem'}}>
                ❌ {wrongCount} / {MAX_WRONG} wrong
              </span>
              <span className="badge fw-bold" style={{background:'#d1fae5',color:'#065f46',fontSize:'0.9rem',padding:'0.4rem 0.8rem'}}>
                ✅ {word.length - displayWord.filter(c=>c==='_').length} / {word.length} found
              </span>
            </div>

            {/* Hangman SVG */}
            <div className="d-flex justify-content-center mb-2">
              <HangmanSVG wrong={wrongCount}/>
            </div>

            {/* Wrong guesses */}
            {wrongGuesses.length > 0 && (
              <div className="mb-2">
                <small className="text-muted fw-bold">Wrong guesses: </small>
                {wrongGuesses.map(l => (
                  <span key={l} style={{color:'#ef4444',fontWeight:900,marginRight:4,fontSize:'1.1rem'}}>{l}</span>
                ))}
              </div>
            )}

            {/* Word display */}
            <div className="hangman-letters mb-3">
              {displayWord.map((ch, i) => (
                <div key={i} className="letter-blank" style={{color: ch !== '_' ? 'var(--primary)' : 'transparent', borderBottomColor: status === 'lost' && ch === '_' ? '#ef4444' : 'var(--primary)'}}>
                  {status === 'lost' && ch === '_' ? <span style={{color:'#ef4444'}}>{word[i]}</span> : ch}
                </div>
              ))}
            </div>

            {/* Status messages */}
            {status === 'won' && (
              <div className="alert alert-custom mb-3" style={{background:'#d1fae5',color:'#065f46'}}>
                🎉 Amazing! You got it! The word was <strong>{word}</strong>!
              </div>
            )}
            {status === 'lost' && (
              <div className="alert alert-custom mb-3" style={{background:'#fee2e2',color:'#991b1b'}}>
                💀 Oh no! The word was <strong>{word}</strong>. Better luck next time!
              </div>
            )}

            {/* Keyboard */}
            {status === 'playing' && (
              <div className="hangman-keyboard">
                {ALPHABET.map(l => {
                  const isGuessed = guessed.has(l);
                  const isCorrect = word.includes(l) && isGuessed;
                  const isWrong   = !word.includes(l) && isGuessed;
                  return (
                    <button
                      key={l}
                      className={`hk-key ${isCorrect ? 'correct' : ''} ${isWrong ? 'wrong' : ''}`}
                      disabled={isGuessed}
                      onClick={() => handleGuess(l)}
                    >{l}</button>
                  );
                })}
              </div>
            )}

            <div className="d-flex gap-2 justify-content-center mt-3 flex-wrap">
              <button className="solve-btn" onClick={newGame}>🔄 New Word</button>
              {(status === 'won' || status === 'lost') && (
                <button className="btn fw-bold"
                  style={{ borderRadius: 50, background: '#4f46e5', color: '#fff', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}
                  onClick={() => setShowShare(true)}>
                  📤 Share Result
                </button>
              )}
            </div>
          </div>
        )}
      <SeoContent game="hangman" />
      </div>
    </div>
  );
}
